import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import {
  createAdminWorkType,
  deleteAdminWorkType,
  getWorkTypePositionIds,
  listAdminWorkTypes,
  listWorkTypePositionIndex,
  patchAdminWorkType,
  reorderAdminWorkTypes,
  setWorkTypePositionIds,
  type CreateWorkTypeRequest,
  type PatchWorkTypeRequest,
  type WorkType,
  type WorkTypePositionIndex,
} from "@/api/admin";

export const ADMIN_WORK_TYPES_KEY = ["admin-work-types"] as const;
export const WORK_TYPE_POSITIONS_KEY = (workTypeId: number) =>
  ["admin-work-types", workTypeId, "positions"] as const;
export const WORK_TYPE_POSITION_INDEX_KEY = ["admin-work-types", "position-index"] as const;

function noRetryOnAuth(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
  return failureCount < 1;
}

export function useAdminWorkTypes(enabled: boolean = true) {
  return useQuery<WorkType[], Error>({
    queryKey: ADMIN_WORK_TYPES_KEY,
    queryFn: listAdminWorkTypes,
    retry: noRetryOnAuth,
    staleTime: 60_000,
    enabled,
  });
}

export function useCreateAdminWorkType() {
  const qc = useQueryClient();
  return useMutation<WorkType, Error, CreateWorkTypeRequest>({
    mutationFn: createAdminWorkType,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_WORK_TYPES_KEY }),
  });
}

export interface PatchWorkTypeVars {
  id: number;
  body: PatchWorkTypeRequest;
}

export function usePatchAdminWorkType() {
  const qc = useQueryClient();
  return useMutation<WorkType, Error, PatchWorkTypeVars>({
    mutationFn: ({ id, body }) => patchAdminWorkType(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_WORK_TYPES_KEY }),
  });
}

export function useDeleteAdminWorkType() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteAdminWorkType,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_WORK_TYPES_KEY }),
  });
}

export function useReorderAdminWorkTypes() {
  const qc = useQueryClient();
  return useMutation<void, Error, number[]>({
    mutationFn: reorderAdminWorkTypes,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ADMIN_WORK_TYPES_KEY }),
  });
}

export function useWorkTypePositions(workTypeId: number | null) {
  return useQuery<number[], Error>({
    queryKey:
      workTypeId == null
        ? (["admin-work-types", "_none", "positions"] as const)
        : WORK_TYPE_POSITIONS_KEY(workTypeId),
    queryFn: () => (workTypeId == null ? Promise.resolve([]) : getWorkTypePositionIds(workTypeId)),
    retry: noRetryOnAuth,
    staleTime: 30_000,
    enabled: workTypeId != null,
  });
}

export function useWorkTypePositionIndex() {
  return useQuery<WorkTypePositionIndex[], Error>({
    queryKey: WORK_TYPE_POSITION_INDEX_KEY,
    queryFn: listWorkTypePositionIndex,
    retry: noRetryOnAuth,
    staleTime: 30_000,
  });
}

export interface SetWorkTypePositionsVars {
  workTypeId: number;
  ids: number[];
}

export function useSetWorkTypePositions() {
  const qc = useQueryClient();
  return useMutation<number[], Error, SetWorkTypePositionsVars, { previous: number[] }>({
    mutationFn: ({ workTypeId, ids }) => setWorkTypePositionIds(workTypeId, ids),
    onMutate: async (vars) => {
      const key = WORK_TYPE_POSITIONS_KEY(vars.workTypeId);
      await qc.cancelQueries({ queryKey: key });
      const previous = [...(qc.getQueryData<number[]>(key) ?? [])];
      qc.setQueryData(key, [...vars.ids]);
      return { previous };
    },
    onError: (_error, vars, context) => {
      qc.setQueryData(WORK_TYPE_POSITIONS_KEY(vars.workTypeId), context?.previous ?? []);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["positions"] });
    },
    onSettled: (_data, _error, vars) => {
      void qc.invalidateQueries({ queryKey: WORK_TYPE_POSITIONS_KEY(vars.workTypeId) });
      void qc.invalidateQueries({ queryKey: WORK_TYPE_POSITION_INDEX_KEY });
    },
  });
}
